/**
 * projectHealth.js — the traffic light on a project, and the colours it uses.
 *
 * green   fine. The default, stored as NULL so nothing had to be backfilled.
 * yellow  something the team should be aware of.
 * red     a major issue needing action now.
 *
 * Yellow and red REQUIRE a comment explaining the issue: a colour on its own
 * tells the next person there is a problem without telling them what it is.
 * That comment is stored with the same severity, so it shows in the matching
 * colour in the Comments column, the thread and the detail panel.
 */
export const HEALTH = {
  green: {
    key: 'green',
    label: 'Good',
    fg: '#1B5E20',
    bg: '#E8F5E9',
    dot: '#2E7D32',
    tint: '#F4FAF4',
    tintAlt: '#EBF4EB',
    hover: '#D1FAE5',
    blurb: 'No issues — the normal state.',
  },
  yellow: {
    key: 'yellow',
    label: 'Warning',
    fg: '#8D6E00',
    bg: '#FFF8E1',
    dot: '#F9A825',
    tint: '#FFF8E1',
    tintAlt: '#FFF3CC',
    hover: '#FFE7A0',
    blurb: 'Something the team should be aware of.',
  },
  red: {
    key: 'red',
    label: 'Critical',
    fg: '#B71C1C',
    bg: '#FFEBEE',
    dot: '#C62828',
    tint: '#FFEBEE',
    tintAlt: '#FFE1E6',
    hover: '#FFCDD2',
    blurb: 'A major issue needing action now.',
  },
};

/**
 * The row tint for a table row, alternating so the zebra striping survives.
 *
 * `tint` is applied to the CELLS, not the row: the PO and invoice column
 * groups paint their own backgrounds (bg-[#FAFBF7], bg-[#FFFBEB]), so a colour
 * on the <tr> was covered up in exactly those columns and the highlight looked
 * half-applied. See the [data-health] rule in src/index.css.
 */
export const rowTint = (project, rowIndex = 0) => {
  const h = HEALTH[healthOf(project)];
  return rowIndex % 2 === 0 ? h.tint : h.tintAlt;
};

/** The hover colour: a deeper shade of the row's OWN flag. It used to be one
 *  green for everything, so pointing at a red project turned it green. */
export const rowHover = (project) => HEALTH[healthOf(project)].hover;

/** The three, in escalation order, for pickers. */
export const HEALTH_ORDER = ['green', 'yellow', 'red'];

/** Stored NULL/'' means green. */
export const healthOf = (project) => {
  const h = String((project && (project.health ?? project.Health)) || '').toLowerCase();
  return HEALTH[h] ? h : 'green';
};

/** Yellow and red cannot be set without saying why. */
export const needsReason = (key) => key === 'yellow' || key === 'red';

/** Colour for a comment: flag comments inherit their flag's colour. */
export const severityTone = (severity) => HEALTH[String(severity || '').toLowerCase()] || null;
